import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import axios from 'axios';
import Layout from '../../components/Layout/Layout';
import styles from './ReceiptPage.module.scss';
import { API_BASE_URL } from '../../config/api';

interface ReceiptData {
  id: number;
  order_id: number;
  payment_id: string | null;
  amount: number;
  description: string;
  status: string;
  created_at: string;
  paid_at: string | null;
  receipt_url: string | null;
}

const ReceiptPage: React.FC = () => {
  const { receiptId } = useParams<{ receiptId: string }>();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState<ReceiptData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/api/v1/payment/receipt/${receiptId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        });
        setReceipt(response.data);
      } catch (err: any) {
        setError(err.response?.data?.detail || "Ошибка при загрузке чека");
      } finally {
        setLoading(false);
      }
    };

    if (receiptId) {
      fetchReceipt();
    }
  }, [receiptId, navigate]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU');
  };

  const formatDescription = (description: string) => {
    return description.split('\n').map((line, index) => (
      <div key={index} style={{
        marginBottom: line.trim() ? '4px' : '8px',
        fontFamily: line.startsWith('  ') ? 'monospace' : 'inherit',
        fontSize: line.startsWith('  ') ? '14px' : '16px'
      }}>
        {line}
      </div>
    ));
  };

  if (loading) {
    return (
      <Layout>
        <div className={styles.container}>
          <div className={styles.loading}>Загрузка чека...</div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className={styles.container}>
          <div className={styles.error}>
            <h2>Ошибка</h2>
            <p>{error}</p>
            <button onClick={() => navigate('/payment')} className={styles.button}>
              Вернуться к оплате
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (!receipt) {
    return (
      <Layout>
        <div className={styles.container}>
          <div className={styles.error}>
            <h2>Чек не найден</h2>
            <button onClick={() => navigate('/payment')} className={styles.button}>
              Вернуться к оплате
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.receipt}>
          <div className={styles.header}>
            <h1>Чек №{receipt.id}</h1>
            <div className={styles.status} data-status={receipt.status}>
              {receipt.status === 'pending' && 'Ожидает оплаты'}
              {receipt.status === 'success' && 'Оплачен'}
              {receipt.status === 'failed' && 'Ошибка оплаты'}
            </div>
          </div>

          <div className={styles.info}>
            <div className={styles.row}>
              <span className={styles.label}>Номер заказа:</span>
              <span className={styles.value}>{receipt.order_id}</span>
            </div>
            <div className={styles.row}>
              <span className={styles.label}>Сумма:</span>
              <span className={styles.value}>{receipt.amount.toFixed(2)} руб</span>
            </div>
            <div className={styles.row}>
              <span className={styles.label}>Дата создания:</span>
              <span className={styles.value}>{formatDate(receipt.created_at)}</span>
            </div>
            {receipt.paid_at && (
              <div className={styles.row}>
                <span className={styles.label}>Дата оплаты:</span>
                <span className={styles.value}>{formatDate(receipt.paid_at)}</span>
              </div>
            )}
            {receipt.payment_id && (
              <div className={styles.row}>
                <span className={styles.label}>ID платежа:</span>
                <span className={styles.value}>{receipt.payment_id}</span>
              </div>
            )}
          </div>

          <div className={styles.description}>
            <h3>Детали заказа:</h3>
            <div className={styles.descriptionContent}>
              {formatDescription(receipt.description)}
            </div>
          </div>

          {receipt.receipt_url && (
            <div className={styles.receiptLink}>
              <h3>Ссылка на чек:</h3>
              <a href={receipt.receipt_url} target="_blank" rel="noopener noreferrer" className={styles.link}>
                Открыть чек в Тинькофф
              </a>
            </div>
          )}

          <div className={styles.actions}>
            <button onClick={() => navigate('/payment')} className={styles.button}>
              Создать новый заказ
            </button>
            <button onClick={() => window.print()} className={styles.button}>
              Распечатать чек
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ReceiptPage;
